import type { HandoffReason, Report, ReportPeriod } from "../../lib/types";
import { type Cell, type Format, type Sheet, workbook } from "../../lib/xlsx";

export interface ReportContext {
  company: string;
  /** "Del 28 sept al 27 oct". */
  period: string;
  range: { from: string; to: string };
  names: Map<string, string>;
  /** The advisor's name when it is their own report. */
  own: string | null;
  reasons: Record<HandoffReason | "other", string>;
}

type Metric = {
  label: string;
  format: Format;
  pick: (p: ReportPeriod) => number | null;
  /** Fewer is better (waits, rejections, days to close). */
  lower?: boolean;
};

const METRICS: Metric[] = [
  { label: "Conversaciones nuevas", format: "int", pick: (p) => p.conversations.started },
  { label: "Mensajes de clientes", format: "int", pick: (p) => p.conversations.inbound },
  { label: "Pasaron a una persona", format: "int", pick: (p) => p.conversations.handoffs },
  { label: "Atendidas por Lumi", format: "int", pick: (p) => p.conversations.lumi?.answered ?? null },
  { label: "Resueltas por Lumi sola", format: "int", pick: (p) => p.conversations.lumi?.solvedAlone ?? null },
  { label: "Mensajes de Lumi usados", format: "int", pick: (p) => p.conversations.lumi?.messages ?? null },
  { label: "Espera típica de respuesta (min)", format: "dec1", pick: (p) => p.team.medianWaitMinutes, lower: true },
  { label: "Casos nuevos", format: "int", pick: (p) => p.postSale.cases.opened, lower: true },
  { label: "Casos cerrados", format: "int", pick: (p) => p.postSale.cases.closed },
  { label: "Días para cerrar un caso", format: "dec1", pick: (p) => p.postSale.cases.averageDaysToClose, lower: true },
  { label: "Solicitudes de repuesto", format: "int", pick: (p) => p.postSale.parts.requested },
  { label: "Repuestos que llegaron", format: "int", pick: (p) => p.postSale.parts.arrived },
  {
    label: "Días hasta que llega un repuesto",
    format: "dec1",
    pick: (p) => p.postSale.parts.averageDaysToArrive,
    lower: true,
  },
  { label: "Cotizaciones enviadas", format: "int", pick: (p) => p.sales.sent },
  { label: "Cotizaciones aceptadas", format: "int", pick: (p) => p.sales.accepted },
  { label: "Cotizaciones rechazadas", format: "int", pick: (p) => p.sales.rejected, lower: true },
  { label: "Tasa de cierre", format: "pct", pick: (p) => p.sales.closeRate },
  { label: "Aceptado en colones", format: "crc", pick: (p) => p.sales.acceptedAmount.CRC },
  { label: "Aceptado en dólares", format: "usd", pick: (p) => p.sales.acceptedAmount.USD },
];

/** The change from the previous period, green when it is good news and red when it is not. */
function change(now: number | null, before: number | null, lower = false): Cell {
  if (now == null || before == null || before === 0) return null;
  const c = (now - before) / before;
  if (!c) return { value: 0, tone: "flat" };
  return { value: c, tone: lower === c < 0 ? "good" : "bad" };
}

const round1 = (n: number | null) => (n == null ? null : Math.round(n * 10) / 10);

/** The report as a workbook: a summary, then one sheet per list, each with its chart. */
export function reportWorkbook(r: Report, ctx: ReportContext): Blob {
  const made = new Date().toLocaleString("es-CR", {
    timeZone: "America/Costa_Rica",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  const title = (what: string) => (ctx.own ? `${what} · ${ctx.own}` : `${what} · ${ctx.company}`);
  const span = `${r.days} ${r.days === 1 ? "día" : "días"}`;
  const subtitle = `${ctx.period} (${span}) · generado el ${made}`;
  const metrics = METRICS.filter((m) => m.pick(r.current) !== null || m.pick(r.previous) !== null);
  const sheets: Sheet[] = [
    {
      name: "Resumen",
      title: title(ctx.own ? "Mis números" : "Reporte"),
      subtitle,
      columns: [
        { header: "Métrica", width: 36 },
        { header: "Este período", format: "int", width: 16 },
        { header: "Período anterior", format: "int", width: 18 },
        { header: "Cambio", format: "change", width: 12 },
      ],
      rows: metrics.map((m) => {
        const now = m.pick(r.current);
        const before = m.pick(r.previous);
        // Each row in its own format: the column's format is only the default.
        return [m.label, cell(now, m.format), cell(before, m.format), change(now, before, m.lower)];
      }),
      note: `Comparado con los ${span} justo antes. Verde es buena noticia (más ventas, menos espera); rojo, mala.`,
    },
    {
      name: "Equipo",
      title: title("Equipo"),
      subtitle,
      columns: [
        { header: "Asesor" },
        { header: "Conversaciones", format: "int" },
        { header: "Respuestas", format: "int" },
        { header: "Espera típica (min)", format: "dec1" },
        { header: "Respondidas en 15 min", format: "pct" },
      ],
      rows: r.current.team.agents.map((a) => [
        ctx.names.get(a.userId) ?? "Persona que ya no está",
        a.conversations,
        a.replies,
        round1(a.medianWaitMinutes),
        a.within15,
      ]),
      note: "La espera va desde el mensaje del cliente (o desde que Lumi lo pasó) hasta la respuesta de una persona.",
      chart: { title: "Espera típica por asesor (minutos)", labels: 0, values: 3 },
    },
    {
      name: "Traspasos",
      title: title("Por qué pasaron a una persona"),
      subtitle,
      columns: [
        { header: "Motivo", width: 40 },
        { header: "Conversaciones", format: "int" },
      ],
      rows: Object.entries(r.current.conversations.byReason)
        .map(([k, v]): [string, number] => [ctx.reasons[k as HandoffReason] ?? ctx.reasons.other, v ?? 0])
        .sort((a, b) => b[1] - a[1]),
      chart: { title: "Conversaciones que pasaron a una persona", labels: 0, values: 1 },
    },
  ];
  if (r.current.team.areas)
    sheets.push({
      name: "Áreas",
      title: title("Conversaciones que Lumi pasó a cada área"),
      subtitle,
      columns: [
        { header: "Área", width: 32 },
        { header: "Conversaciones", format: "int" },
      ],
      rows: Object.entries(r.current.team.areas).sort((a, b) => b[1] - a[1]),
      chart: { title: "Conversaciones por área", labels: 0, values: 1 },
    });
  sheets.push(
    {
      name: "Postventa por estado",
      title: title("Días promedio en cada estado"),
      subtitle,
      columns: [
        { header: "Estado", width: 28 },
        { header: "Días promedio", format: "dec1" },
        { header: "Cambios medidos", format: "int" },
      ],
      rows: r.current.postSale.cases.byStatus.map((s) => [s.status, round1(s.averageDays), s.count]),
      chart: { title: "Días promedio en cada estado", labels: 0, values: 1 },
    },
    {
      name: "Lo más cotizado",
      title: title("Lo más cotizado"),
      subtitle,
      columns: [
        { header: "Producto", width: 40 },
        { header: "Cotizaciones", format: "int" },
        // Decimals only when something was quoted by the meter or the kilo.
        {
          header: "Cantidad",
          format: r.current.sales.topProducts.every((t) => Number.isInteger(t.quantity)) ? "int" : "dec1",
        },
      ],
      rows: r.current.sales.topProducts.map((t) => [t.label, t.quotes, t.quantity]),
      chart: { title: "Cotizaciones por producto", labels: 0, values: 1 },
    },
  );
  return workbook(sheets);
}

/** A number that keeps its own format in a mixed column ("Este período" has counts and money). */
function cell(value: number | null, format: Format): Cell {
  if (value == null) return null;
  return { value: format === "dec1" ? round1(value) : value, format };
}
